import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Receipt, CheckCircle2, Send as SendIcon, PenLine, Circle } from "lucide-react";
import { toast } from "sonner";

const STAGES = [
  { key: "keine",       label: "Keine",       icon: Circle,        color: "text-muted-foreground" },
  { key: "erstellt",    label: "Erstellt",    icon: PenLine,       color: "text-sky-600 dark:text-sky-400" },
  { key: "abgesendet",  label: "Abgesendet",  icon: SendIcon,      color: "text-amber-600 dark:text-amber-400" },
  { key: "bezahlt",     label: "Bezahlt",     icon: CheckCircle2,  color: "text-emerald-600 dark:text-emerald-400" },
];

function fmtEUR(n) {
  if (n === null || n === undefined || n === "") return "";
  try { return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(n); }
  catch { return `${n} €`; }
}

export function InvoiceStatus({ projectId, initial, onChange, isStaff }) {
  const [inv, setInv] = useState(initial || { status: "keine" });
  const [amt, setAmt] = useState(inv.amount || "");
  const [saving, setSaving] = useState(false);

  const save = async (nextStatus, nextAmt = amt) => {
    if (!isStaff) return;
    setSaving(true);
    try {
      const payload = { status: nextStatus };
      const parsed = parseFloat(String(nextAmt).replace(",", "."));
      if (!Number.isNaN(parsed)) payload.amount = parsed;
      const { data } = await api.patch(`/projects/${projectId}/invoice`, payload);
      setInv(data);
      onChange?.(data);
      toast.success(`Rechnung: ${STAGES.find((s) => s.key === nextStatus)?.label || nextStatus}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const currentStage = STAGES.find((s) => s.key === (inv.status || "keine")) || STAGES[0];
  const CurrentIcon = currentStage.icon;

  return (
    <div className="rounded border border-border/70 mt-2 p-3" data-testid={`invoice-${projectId}`}>
      <div className="flex items-center gap-2 mb-2">
        <Receipt className="h-4 w-4 text-primary" />
        <span className="text-sm font-medium">Rechnung</span>
        <span className={`text-[11px] font-semibold uppercase tracking-wider ${currentStage.color} inline-flex items-center gap-1`}>
          <CurrentIcon className="h-3 w-3" /> {currentStage.label}
        </span>
        {inv.amount ? <span className="text-[11px] text-muted-foreground ml-1">{fmtEUR(inv.amount)}</span> : null}
        {inv.updated_at && (
          <span className="text-[10px] text-muted-foreground ml-auto">
            {inv.updated_by_name} · {new Date(inv.updated_at).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" })}
          </span>
        )}
      </div>
      {isStaff ? (
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-md border border-border overflow-hidden" data-testid={`invoice-segments-${projectId}`}>
            {STAGES.map((s) => {
              const active = (inv.status || "keine") === s.key;
              const Icon = s.icon;
              return (
                <button
                  key={s.key}
                  type="button"
                  disabled={saving}
                  onClick={() => save(s.key)}
                  className={`px-2.5 py-1 text-[11px] font-medium inline-flex items-center gap-1 transition-colors ${active ? "bg-primary text-primary-foreground" : "hover:bg-secondary/60"}`}
                  data-testid={`invoice-set-${projectId}-${s.key}`}
                >
                  <Icon className="h-3 w-3" /> {s.label}
                </button>
              );
            })}
          </div>
          <div className="inline-flex items-center gap-1">
            <Input
              type="text"
              inputMode="decimal"
              value={amt}
              onChange={(e) => setAmt(e.target.value)}
              onBlur={() => { if (String(amt) !== String(inv.amount || "")) save(inv.status || "keine", amt); }}
              placeholder="Betrag (€)"
              className="h-7 text-[11px] w-28"
              data-testid={`invoice-amount-${projectId}`}
            />
          </div>
        </div>
      ) : (
        <div className="text-[11px] text-muted-foreground">
          Rechnungsstatus wird vom Team gepflegt.
        </div>
      )}
    </div>
  );
}
